import { Observable, Subscription } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';

import { PaymentRecommendationModel } from '../models';

export interface PaymentRecommendationRequests {
  getRecommendations: (documentId: number) => Observable<PaymentRecommendationModel[]>;
  dismiss: (documentId: number, paymentId: number) => Observable<any>;
  book: (documentId: number, paymentId: number) => Observable<any>;
}

export function getTopPaymentRecommendation(recommendations: PaymentRecommendationModel[]): PaymentRecommendationModel {
  return (recommendations || []).reduce((top: PaymentRecommendationModel, item: PaymentRecommendationModel) => {
    return !top || (item.score || 0) > (top.score || 0) ? item : top;
  }, null);
}

export class PaymentRecommendationHandler {
  public recommendations: PaymentRecommendationModel[] = [];
  public loading: 'dismiss' | 'book' = null;
  public documentId: number = null;

  private requestSubscription: Subscription;
  private destroyed = false;

  constructor(
    private readonly requests: PaymentRecommendationRequests,
    private readonly destroy$: Observable<any>,
    private readonly onChange: () => void = () => {},
  ) {
    this.destroy$.pipe(take(1)).subscribe(() => this.destroyed = true);
  }

  public load(documentId: number): void {
    this.cancelRequest();
    if (documentId !== this.documentId) {
      this.documentId = documentId;
      this.recommendations = [];
      this.loading = null;
    }

    if (!documentId || this.loading) { return; }

    this.requestSubscription = this.requests.getRecommendations(documentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((recommendations: PaymentRecommendationModel[]) => {
        if (this.documentId !== documentId) { return; }
        this.setRecommendation(getTopPaymentRecommendation(recommendations));
        this.notifyChange();
      }, () => {});
  }

  public dismiss(recommendation: PaymentRecommendationModel): void {
    this.runAction('dismiss', recommendation, this.requests.dismiss);
  }

  public book(recommendation: PaymentRecommendationModel, onBooked?: () => void): void {
    this.runAction('book', recommendation, this.requests.book, onBooked);
  }

  public trackByPaymentId(index: number, recommendation: PaymentRecommendationModel): number {
    return recommendation.paymentId;
  }

  private runAction(
    action: 'dismiss' | 'book',
    recommendation: PaymentRecommendationModel,
    request: (documentId: number, paymentId: number) => Observable<any>,
    onSuccess?: () => void,
  ): void {
    const documentId = this.documentId;
    this.cancelRequest();
    this.loading = action;

    request(documentId, recommendation.paymentId)
      .pipe(
        finalize(() => {
          this.loading = null;
          this.notifyChange();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.documentId === documentId) {
          this.setRecommendation(null);
        }
        if (onSuccess) { onSuccess(); }
      }, () => {});
  }

  private setRecommendation(recommendation: PaymentRecommendationModel): void {
    this.recommendations = recommendation ? [recommendation] : [];
  }

  private cancelRequest(): void {
    if (this.requestSubscription) {
      this.requestSubscription.unsubscribe();
      this.requestSubscription = null;
    }
  }

  private notifyChange(): void {
    if (!this.destroyed) { this.onChange(); }
  }
}
